/**
 * useTrainingSessionDetection - Return-to-App Detection Hook
 * 
 * Detects when a user returns to OptiBay after opening external training.
 * Triggers completion verification flow when user has been away >= 10 seconds.
 * 
 * Usage:
 *   useTrainingSessionDetection({
 *     onReturn: (elapsedSeconds) => { showCompletionDialog(); },
 *     enabled: hasPendingSessions,
 *   });
 */

import { useEffect, useRef, useCallback } from 'react';

interface UseTrainingSessionDetectionOptions {
  /** Called when user returns to app after being away >= 10s */
  onReturn: (elapsedSeconds: number) => void;
  /** Whether detection is enabled */
  enabled: boolean;
  /** Minimum seconds away to trigger (default: 10) */
  minSecondsAway?: number;
}

export function useTrainingSessionDetection({
  onReturn,
  enabled,
  minSecondsAway = 10,
}: UseTrainingSessionDetectionOptions) {
  const visibilityRef = useRef<'visible' | 'hidden'>('visible');
  const hiddenAtRef = useRef<number | null>(null);
  const callbackRef = useRef(onReturn);
  
  // Keep callback ref current
  useEffect(() => {
    callbackRef.current = onReturn;
  }, [onReturn]);
  
  const handleVisibilityChange = useCallback(() => {
    if (!enabled) return;
    
    if (document.visibilityState === 'hidden') {
      // User left the app
      visibilityRef.current = 'hidden';
      hiddenAtRef.current = Date.now();
    } else if (visibilityRef.current === 'hidden') {
      // User returned to app
      const elapsed = hiddenAtRef.current 
        ? (Date.now() - hiddenAtRef.current) / 1000 
        : 0;
      
      // Only trigger if they were away long enough
      if (elapsed >= minSecondsAway) {
        callbackRef.current(elapsed);
      }
      
      visibilityRef.current = 'visible';
      hiddenAtRef.current = null;
    }
  }, [enabled, minSecondsAway]);
  
  useEffect(() => {
    if (!enabled) return;
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Also listen to focus events as backup
    const handleFocus = () => {
      if (visibilityRef.current === 'hidden') {
        handleVisibilityChange();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, [enabled, handleVisibilityChange]);
  
  return {
    /** Whether user is currently away */
    isAway: visibilityRef.current === 'hidden',
    /** Reset the hidden timestamp */
    resetTimer: () => {
      hiddenAtRef.current = null;
      visibilityRef.current = 'visible';
    },
  };
}
